import { CalendarBlank, LockSimple } from '@/components/icons';
import type { Glyph, ResultReading } from '@/domain/tags';
import { m } from '@/i18n';
import { Msg } from '@/lib/locale-context';
import { GlyphTip } from './glyph-tip';
import { Phrase } from './phrase';
import styles from './values.module.css';
import { hiddenItemWords, itemWords, stubItemWords } from './words';

export type ItemValueProps = { className?: string } & (
  | {
      hidden?: false;
      stub?: false;
      title: string;
      /** The item's glyph, decided once by the domain (`glyphOf`,
       *  src/domain/tags.ts), as every row's dot is. */
      glyph: Glyph;
      result?: never;
      endsAfter?: never;
    }
  | {
      /** An item the viewer sees only as its stub (docs/MODEL.md §6): its
       *  name, a neutral glyph (its health is content), and a lock sign after
       *  the name meaning "you have no access". */
      stub: true;
      hidden?: false;
      title: string;
      /** Its result, which a stub carries (docs/MODEL.md §6): the value
       *  recedes, struck, when it is done or cancelled. Defaults to open. */
      result?: ResultReading;
      /** It now ends after this item's end while open: the past-due date
       *  sign, with no date. */
      endsAfter?: boolean;
      glyph?: never;
    }
  | {
      /** An item the viewer cannot see: a lock sign and "Not visible",
       *  never a title, a date or a name. */
      hidden: true;
      stub?: false;
      /** The one thing it may report about itself: it was called off. */
      cancelled?: boolean;
      title?: never;
      glyph?: never;
      result?: never;
      endsAfter?: never;
    }
);

/**
 * An item (docs/DESIGN_SYSTEM.md "Values and entries"): its glyph and its
 * title, the same wherever it is referenced. The glyph is the rows' dot, its
 * colour set by globals.css from `data-health` / `data-tense`; a closed item
 * recedes as a row does (the global `[data-closed]` rule), its title struck.
 * The `hidden` variant renders the lock sign and "Not visible", receding when
 * cancelled, and accepts no title. The `stub` variant is an item the viewer
 * sees only as its stub: its title, a neutral glyph, the lock sign after the
 * title, receding when done or cancelled and wearing the past-due date sign
 * (no date) when it is open and now ends after this item. Display only: the
 * row it sits on carries the act. Server-safe.
 */
export function ItemValue(props: ItemValueProps) {
  if (props.hidden) {
    const cancelled = props.cancelled ?? false;
    return (
      <span
        className={[styles.value, styles.item, props.className].filter(Boolean).join(' ')}
        data-value="hidden-item"
        data-closed={cancelled ? 'true' : undefined}
      >
        <LockSimple className={styles.sign} aria-hidden />
        <Phrase
          message={hiddenItemWords({ cancelled })}
          shown={{
            hidden: (
              <span className={`${styles.text} ${styles.title} ${styles.notVisible}`}>
                <Msg message={m('values.item.hidden')} />
              </span>
            ),
          }}
        />
      </span>
    );
  }
  if (props.stub) {
    const result = props.result ?? 'open';
    // A done or cancelled stub recedes; its end no longer says anything.
    const endsAfter = result === 'open' && (props.endsAfter ?? false);
    return (
      <span
        className={[styles.value, styles.item, props.className].filter(Boolean).join(' ')}
        data-value="stub-item"
        data-closed={result !== 'open' ? 'true' : undefined}
      >
        <span className={styles.glyph} aria-hidden />
        <Phrase
          message={stubItemWords({ title: props.title, result, endsAfter })}
          shown={{ title: <span className={`${styles.text} ${styles.title}`}>{props.title}</span> }}
        />
        <LockSimple className={styles.sign} aria-hidden />
        {endsAfter && (
          <span className={`${styles.signBox} ${styles.endsAfter}`} aria-hidden>
            <CalendarBlank className={styles.sign} />
            <span className={styles.signDot} />
          </span>
        )}
      </span>
    );
  }
  const { title, glyph, className } = props;
  return (
    <span
      className={[styles.value, styles.item, className].filter(Boolean).join(' ')}
      data-value="item"
      data-health={glyph.health}
      data-tense={glyph.tense}
      data-closed={glyph.closed ? 'true' : undefined}
    >
      <GlyphTip states={glyph}>
        <span className={styles.glyph} aria-hidden />
      </GlyphTip>
      <Phrase
        message={itemWords({ title, glyph })}
        shown={{ title: <span className={`${styles.text} ${styles.title}`}>{title}</span> }}
      />
    </span>
  );
}

/**
 * An item's glyph alone (docs/DESIGN_SYSTEM.md "Values and entries"), where a
 * row names the items elsewhere and shows only the worst of their glyphs (the
 * item page's Relationships door row). The same dot as `ItemValue`'s, coloured
 * by its own data-health / data-tense. Without a `glyph` it is the neutral dot
 * a stub wears, for a surface that names an item without reading how it
 * stands (a reference in prose, docs/PROSE.md §5.3). It says nothing to a
 * screen reader: the row's words say what it summarises. Server-safe.
 */
export function ItemGlyph({ glyph, className }: { glyph?: Glyph; className?: string }) {
  return (
    <span
      className={[styles.glyph, className].filter(Boolean).join(' ')}
      data-value="glyph"
      data-health={glyph?.health}
      data-tense={glyph?.tense}
      aria-hidden
    />
  );
}
